import React, { useState, useEffect, useMemo } from 'react';
import {
    Paper,
    Box,
    Button,
    TextField,
    Typography,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Autocomplete,
    Chip,
    CircularProgress,
    MenuItem,
    Alert,
} from '@mui/material';
import styles from './ChecklistSetting.module.scss';

// ─── Types ────────────────────────────────────────────────────────────────────

export type AssignmentType = 'security_role' | 'users';

export interface UserOption {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
}

export interface AssignmentGroupFormValues {
    id?: number;
    groupName: string;
    assignmentType: AssignmentType;
    roleId: string;       // selected security-role id (string for select value)
    selectedUsers: UserOption[];
}

interface AssignmentGroupFormProps {
    /** Prefilled values when editing an existing group */
    editValues?: AssignmentGroupFormValues | null;
    /** Called when the user clicks Back or Cancel */
    onBack: () => void;
    /** Called when the form is submitted successfully */
    onSave: (values: AssignmentGroupFormValues) => Promise<void>;
    /** Available job-code / role options */
    dynamicRoles: { id: number; label: string; backgroundColor: string; textColor: string }[];
    /** Available users (fetched by parent) */
    users: UserOption[];
    usersLoading: boolean;
    canEdit?: boolean;
    canExecute?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const AssignmentGroupForm: React.FC<AssignmentGroupFormProps> = ({
    editValues,
    onBack,
    onSave,
    dynamicRoles,
    users,
    usersLoading,
    canEdit = false,
    canExecute = false,
}) => {
    const isEditing = Boolean(editValues?.id);

    // ── Local form state ──────────────────────────────────────────────────────
    const [groupName, setGroupName] = useState(editValues?.groupName ?? '');
    const [assignmentType, setAssignmentType] = useState<AssignmentType>(
        editValues?.assignmentType ?? 'security_role'
    );
    const [roleId, setRoleId] = useState<string>(editValues?.roleId ?? '');
    const [selectedUsers, setSelectedUsers] = useState<UserOption[]>(
        editValues?.selectedUsers ?? []
    );

    // ── Validation state ──────────────────────────────────────────────────────
    const [errors, setErrors] = useState<{
        groupName?: string;
        roleId?: string;
        selectedUsers?: string;
    }>({});
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    // ── Sync when editValues change (e.g. parent refreshes) ──────────────────
    useEffect(() => {
        if (editValues) {
            setGroupName(editValues.groupName);
            setAssignmentType(editValues.assignmentType ?? 'security_role');
            setRoleId(editValues.roleId ?? '');
            setSelectedUsers(editValues.selectedUsers ?? []);
        }
    }, [editValues?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Assignment type change: ask before clearing sibling selection ─────────
    const handleAssignmentTypeChange = (newType: AssignmentType) => {
        if (newType === assignmentType) return;
        // Clear the opposite selection silently
        if (newType === 'security_role') {
            setSelectedUsers([]);
            setErrors((prev) => ({ ...prev, selectedUsers: undefined }));
        } else {
            setRoleId('');
            setErrors((prev) => ({ ...prev, roleId: undefined }));
        }
        setAssignmentType(newType);
    };

    // ── Validation ────────────────────────────────────────────────────────────
    const validate = (): boolean => {
        const newErrors: typeof errors = {};

        if (!groupName.trim()) {
            newErrors.groupName = 'Group name is required';
        } else if (groupName.length > 200) {
            newErrors.groupName = 'Maximum 200 characters allowed';
        }

        if (assignmentType === 'security_role') {
            if (!roleId) newErrors.roleId = 'A security role must be selected';
        } else {
            if (selectedUsers.length === 0)
                newErrors.selectedUsers = 'At least one user must be selected';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        setSaveError(null);
        try {
            await onSave({
                id: editValues?.id,
                groupName: groupName.trim(),
                assignmentType,
                roleId,
                selectedUsers,
            });
        } catch (err: any) {
            setSaveError(err?.error || err?.message || 'Failed to save assignment group');
        } finally {
            setSaving(false);
        }
    };

    const canSave = isEditing ? canEdit : canExecute;

    // Prefer the loaded users list for labels; prefilled members may only carry an id
    const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
    const getUserLabel = (u: UserOption) => {
        const resolved = usersById.get(u.id) ?? u;
        return (
            `${resolved.firstName} ${resolved.lastName}`.trim() ||
            resolved.email ||
            `User ${resolved.id}`
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Paper
            elevation={3}
            className={styles.containerr}
            style={{ padding: '20px', backgroundColor: 'whitesmoke' }}
        >
            {/* ── Top bar: Back + Save ── */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Button variant="outlined" onClick={onBack} className={styles.button}>
                    Back
                </Button>
                <Button
                    style={{
                        backgroundColor: canSave ? 'rgb(68, 99, 136)' : 'rgba(68, 99, 136, 0.5)',
                        color: 'white',
                        padding: '5px 15px',
                        textTransform: 'capitalize',
                    }}
                    onClick={handleSave}
                    disabled={!canSave || saving}
                    title={canSave ? 'Save group' : 'You do not have permission to save'}
                >
                    {saving ? <CircularProgress size={18} color="inherit" /> : 'Save'}
                </Button>
            </Box>

            {saveError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {saveError}
                </Alert>
            )}

            <div className={styles.rightPanel}>
                <Typography variant="h6" mb={2}>
                    {isEditing ? 'Edit Assignment Group' : 'New Assignment Group'}
                </Typography>

                {/* ── Group Name ── */}
                <Box mb={2}>
                    <TextField
                        label="Group Name"
                        value={groupName}
                        onChange={(e) => {
                            setGroupName(e.target.value);
                            if (errors.groupName)
                                setErrors((prev) => ({ ...prev, groupName: undefined }));
                        }}
                        fullWidth
                        required
                        size="small"
                        inputProps={{ maxLength: 200 }}
                        error={Boolean(errors.groupName)}
                        helperText={errors.groupName ?? ''}
                        disabled={!canSave}
                    />
                </Box>

                {/* ── Assignment Type ── */}
                <Box mb={2}>
                    <FormControl component="fieldset">
                        <FormLabel component="legend" sx={{ fontWeight: 500, color: 'text.primary', mb: 0.5 }}>
                            Assignment Type
                        </FormLabel>
                        <RadioGroup
                            row
                            value={assignmentType}
                            onChange={(e) =>
                                handleAssignmentTypeChange(e.target.value as AssignmentType)
                            }
                        >
                            <FormControlLabel
                                value="security_role"
                                control={<Radio color="primary" />}
                                label="Security Role"
                                disabled={!canSave}
                            />
                            <FormControlLabel
                                value="users"
                                control={<Radio color="primary" />}
                                label="Users"
                                disabled={!canSave}
                            />
                        </RadioGroup>
                    </FormControl>
                </Box>

                {/* ── Conditional: Security Role OR Users ── */}
                {assignmentType === 'security_role' ? (
                    <Box mb={2}>
                        <TextField
                            select
                            label="Security Role"
                            value={roleId}
                            onChange={(e) => {
                                setRoleId(e.target.value);
                                if (errors.roleId)
                                    setErrors((prev) => ({ ...prev, roleId: undefined }));
                            }}
                            fullWidth
                            required
                            size="small"
                            error={Boolean(errors.roleId)}
                            helperText={errors.roleId ?? ''}
                            disabled={!canSave}
                        >
                            <MenuItem value="">-- Select Security Role --</MenuItem>
                            {Array.isArray(dynamicRoles) &&
                                dynamicRoles.map((role) => (
                                    <MenuItem key={role.id} value={String(role.id)}>
                                        {role.label}
                                    </MenuItem>
                                ))}
                        </TextField>
                    </Box>
                ) : (
                    <Box mb={2}>
                        <Autocomplete
                            multiple
                            options={users}
                            getOptionLabel={getUserLabel}
                            isOptionEqualToValue={(opt, val) => opt.id === val.id}
                            value={selectedUsers}
                            onChange={(_, newVal) => {
                                setSelectedUsers(newVal);
                                if (errors.selectedUsers && newVal.length > 0)
                                    setErrors((prev) => ({ ...prev, selectedUsers: undefined }));
                            }}
                            loading={usersLoading}
                            disabled={!canSave}
                            filterSelectedOptions
                            renderTags={(value, getTagProps) =>
                                value.map((u, index) => (
                                    <Chip
                                        key={u.id}
                                        label={getUserLabel(u)}
                                        size="small"
                                        {...getTagProps({ index })}
                                    />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select Users"
                                    placeholder="Search users…"
                                    size="small"
                                    required
                                    error={Boolean(errors.selectedUsers)}
                                    helperText={errors.selectedUsers ?? ''}
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {usersLoading ? (
                                                    <CircularProgress color="inherit" size={16} />
                                                ) : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                        />
                    </Box>
                )}
            </div>
        </Paper>
    );
};

export default AssignmentGroupForm;
